<template>
  <v-container>
    <v-form :readonly="!canEditSettings">
      <v-card>
        <v-card-title>Yellow Bus</v-card-title>
        <v-card-text>
          <v-row dense>
            <v-col cols="12" md="12" class="bg-blue">
              <v-subheader>Display Yellow Bus Vehicle Type?</v-subheader>
              <v-radio-group v-model="config.displayYellowBusVehicleType" row class="ml-4">
                <v-radio label="Yes" :value="true"></v-radio>
                <v-radio label="No" :value="false"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" md="12">
              <v-subheader
                >Do vehicle owners who are not administrators have authority to assign yellow buses from their own
                location?</v-subheader
              >
              <v-radio-group v-model="config.canVehicleOwnersAssignYellowBus" row class="ml-4">
                <v-radio label="Yes" :value="true"></v-radio>
                <v-radio label="No" :value="false"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" md="12" class="bg-blue">
              <v-subheader>Breakdown of yellow bus budget codes on invoice summary?</v-subheader>
              <v-radio-group v-model="config.breakdownOfBudgetCodesOnInvoiceSummary" row class="ml-4">
                <v-radio label="Yes" :value="true"></v-radio>
                <v-radio label="No" :value="false"></v-radio>
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" md="12">
              <v-subheader>Yellow Bus Budget Codes</v-subheader>
            </v-col>
          </v-row>
          <v-row dense class="px-2">
            <v-col cols="12">
              <div class="mb-5">
                <v-form v-if="isAddingBudgetCode" @submit="addBudgetCodeForm" lazy-validation>
                  <v-row>
                    <v-col cols="3">
                      <v-text-field
                        :error-messages="handleErrors($v.budgetCodeForm.lineNumber)"
                        v-model="budgetCodeForm.lineNumber"
                        label="Line #"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="3">
                      <v-text-field
                        :error-messages="handleErrors($v.budgetCodeForm.budgetCode)"
                        v-model="budgetCodeForm.budgetCode"
                        label="Budget Code"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="3">
                      <v-text-field
                        :error-messages="handleErrors($v.budgetCodeForm.description)"
                        v-model="budgetCodeForm.description"
                        label="Description"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="3">
                      <v-text-field
                        :error-messages="handleErrors($v.budgetCodeForm.costPerMile)"
                        v-model="budgetCodeForm.costPerMile"
                        label="Cost Per Mile"
                        type="number"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <div class="d-flex justify-end">
                    <v-btn type="submit" class="mr-2" color="primary">Save</v-btn>
                    <v-btn @click="clearBudgetCodeForm">Cancel</v-btn>
                  </div>
                </v-form>
                <v-btn v-else @click="isAddingBudgetCode = true" color="primary"> Add Budget Code </v-btn>
              </div>
              <div>
                <v-data-table
                  :headers="budgetCodesHeaders"
                  :items="config.budgetCodes"
                  class="elevation-1"
                  dense
                  disable-sort
                  hide-default-footer
                >
                  <template v-slot:item.costPerMile="{ item }">
                    {{ toMoney(item.costPerMile, 4) }}
                  </template>
                  <template v-slot:item.actions="{ item, index }">
                    <span class="mr-5">&nbsp;</span>
                    <v-icon class="ml-5" @click="removeBudgetCode(index)">mdi-delete</v-icon>
                  </template>
                  <template v-slot:footer>
                    <div class="total-cost">
                      <h3>Total Cost Per Mile:</h3>
                      <h3>{{ toMoney(totalCostPerMile, 4) }}</h3>
                    </div>
                  </template>
                </v-data-table>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-form>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import { required, decimal, minValue } from 'vuelidate/lib/validators';
import { GET_CONFIG, SAVE_YELLOW_BUS_CONFIG } from '@/store/modules/Config/actions';
import promptBeforeLeave from '@/mixins/promptBeforeLeave';
import { handleErrors, toMoney } from '@/util';

export default {
  mixins: [promptBeforeLeave],
  inject: ['eventHub'],
  components: {},
  data: () => ({
    config: {
      displayYellowBusVehicleType: false,
      canVehicleOwnersAssignYellowBus: false,
      breakdownOfBudgetCodesOnInvoiceSummary: false,
      budgetCodes: [],
    },
    budgetCodeForm: {
      lineNumber: null,
      budgetCode: null,
      description: null,
      costPerMile: null,
    },
    budgetCodesHeaders: [
      { text: 'Line #', value: 'lineNumber' },
      { text: 'Budget Code', value: 'budgetCode' },
      { text: 'Description', value: 'description' },
      { text: 'Cost Per Mile', value: 'costPerMile', align: 'right' },
      { text: 'Action', value: 'actions' },
    ],
    isAddingBudgetCode: false,
  }),
  validations() {
    return {
      budgetCodeForm: {
        lineNumber: { required },
        budgetCode: { required },
        description: { required },
        costPerMile: { required, decimal, minValue: minValue(0) },
      },
    };
  },
  computed: {
    ...mapGetters('user', ['me', 'canEditSettings']),
    totalCostPerMile() {
      let total = 0;
      for (const bc of this.config.budgetCodes || []) {
        total += Number(bc.costPerMile) || 0;
      }
      return total;
    },
  },
  async mounted() {
    await this.fetchItems();
  },
  methods: {
    ...mapActions('config', [GET_CONFIG, SAVE_YELLOW_BUS_CONFIG]),
    handleErrors,
    toMoney,
    async fetchItems() {
      this.yellowBusConfig = await this.getConfig('yellowBus');
      this.config = { ...this.config, ...this.yellowBusConfig };
      this.baseFormValue = cloneDeep(this.config);
    },
    async save() {
      try {
        const r = await this.saveYellowBusConfig(this.config);
        if (r && r.done) {
          this.baseFormValue = cloneDeep(this.config);
          this.$myalert.success('Yellow Bus config saved');
        }
      } catch (error) {
        this.$myalert.error(error.message);
      }
    },
    addBudgetCodeForm(e) {
      e.preventDefault();
      this.$v.budgetCodeForm.$touch();

      if (this.$v.budgetCodeForm.$invalid) {
        this.$myalert.error('Please fill in all required fields', true);
        return;
      }
      // ensure 4 decimal place
      this.budgetCodeForm.costPerMile = Math.floor(this.budgetCodeForm.costPerMile * 10000) / 10000;

      this.config.budgetCodes.push(this.budgetCodeForm);
      this.clearBudgetCodeForm();
    },
    async removeBudgetCode(index) {
      this.config.budgetCodes.splice(index, 1);
    },
    clearBudgetCodeForm() {
      this.budgetCodeForm = {
        lineNumber: null,
        budgetCode: null,
        description: null,
        costPerMile: null,
      };
      this.$v.budgetCodeForm.$reset();
      this.isAddingBudgetCode = false;
    },
  },
  created() {
    this.eventHub.$on('saveYellowBusConfig', () => this.save());
  },
  beforeDestroy() {
    this.eventHub.$off('saveYellowBusConfig');
  },
  watch: {
    config: {
      handler(value) {
        this.latestFormValue = cloneDeep(value);
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.bg-blue {
  background-color: aliceblue;
}
.total-cost {
  float: right;
  display: flex;
  flex-direction: row;
  gap: 10px;
  padding: 10px;
}
</style>
